import React, { useState } from 'react';
import { colors, spacing, typography, motion, imagery } from './theme';
import { BrandLogo } from './components/BrandLogo';

const label = {
  fontFamily: typography.label.family,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  fontSize: 11,
  fontWeight: 600,
  color: colors.gold,
};

const display = {
  fontFamily: typography.display.family,
  fontWeight: 500,
  letterSpacing: '-0.015em',
  lineHeight: 1.02,
  color: colors.paper,
};

const container = {
  width: '100%',
  maxWidth: 1280,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: spacing.lg,
  paddingRight: spacing.lg,
  boxSizing: 'border-box',
};

const hairline = { height: 1, width: '100%', background: colors.hairline };

/* StatusStrip */
function StatusStrip() {
  return (
    <div
      data-testid="status-strip"
      style={{
        width: '100%',
        background: colors.onyx,
        borderBottom: `1px solid ${colors.hairlineSoft}`,
        paddingTop: 9,
        paddingBottom: 9,
      }}
    >
      <div
        style={{
          ...container,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: spacing.md,
          ...label,
          color: colors.ash,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: colors.champagne,
              boxShadow: `0 0 8px ${colors.champagne}`,
            }}
          />
          <span style={{ color: colors.bone }}>24/7 Concierge</span>
          <span style={{ color: colors.gold }}>·</span>
          <span>By Appointment Only</span>
        </span>
        <span>Serving Greater Phoenix, Arizona</span>
      </div>
    </div>
  );
}

/* TopNav */
function TopNav() {
  return (
    <nav
      data-testid="top-nav"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'rgba(10,10,11,0.75)',
        backdropFilter: 'blur(16px) saturate(140%)',
        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
        borderBottom: `1px solid ${colors.hairlineSoft}`,
      }}
    >
      <div
        style={{
          ...container,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 18,
          paddingBottom: 18,
        }}
      >
        <a href="#top" data-testid="nav-brand" aria-label="Black-Ops home">
          <BrandLogo variant="horizontal" size={150} color={colors.bone} taglineColor={colors.gold} />
        </a>

        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: spacing.xl, ...label, color: colors.bone }}>
          <a href="#services" data-testid="nav-services">Services</a>
          <a href="#fleet" data-testid="nav-fleet">Fleet</a>
          <a href="#membership" data-testid="nav-membership">Membership</a>
          <a href="#reserve" data-testid="nav-contact">Contact</a>
          <a
            href="#reserve"
            data-testid="nav-open-app"
            style={{
              paddingTop: 11,
              paddingBottom: 11,
              paddingLeft: 22,
              paddingRight: 22,
              border: `1px solid ${colors.champagne}`,
              color: colors.champagne,
              letterSpacing: '0.32em',
              transition: `background ${motion.fast}ms ${motion.easeOut}, color ${motion.fast}ms ${motion.easeOut}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.champagne;
              e.currentTarget.style.color = colors.void;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = colors.champagne;
            }}
          >
            Open App
          </a>
        </div>
      </div>
    </nav>
  );
}

/* Hero */
function Hero() {
  return (
    <section
      id="top"
      data-testid="hero"
      style={{
        position: 'relative',
        minHeight: 'calc(100vh - 110px)',
        overflow: 'hidden',
        backgroundColor: colors.void,
        backgroundImage: `${imagery.heroOverlayGradient}, ${imagery.heroSideGradient}, url("${imagery.heroPhoto}")`,
        backgroundSize: 'cover, cover, cover',
        backgroundPosition: 'center, center, center',
        backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
        display: 'flex',
        alignItems: 'center',
        paddingTop: spacing.xxl,
        paddingBottom: spacing.xxl,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: imagery.grainNoiseDataURI,
          opacity: 0.5,
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />

      <div style={{ ...container, position: 'relative', zIndex: 1, maxWidth: 1280 }}>
        <div
          data-testid="hero-tagline"
          style={{
            ...label,
            color: colors.champagne,
            letterSpacing: '0.42em',
            marginBottom: spacing.lg,
            fontSize: 12,
          }}
        >
          Discretion <span style={{ color: colors.gold, margin: '0 10px' }}>·</span> Excellence <span style={{ color: colors.gold, margin: '0 10px' }}>·</span> Arrival
        </div>

        <h1
          data-testid="hero-title"
          style={{
            ...display,
            fontSize: 'clamp(44px, 7.5vw, 104px)',
            marginTop: 0,
            marginBottom: spacing.lg,
            maxWidth: 920,
            color: colors.paper,
            textShadow: '0 2px 30px rgba(0,0,0,0.55)',
          }}
        >
          Your private chauffeur,<br />
          on call.
        </h1>

        <p
          data-testid="hero-subtitle"
          style={{
            fontFamily: typography.body.family,
            fontSize: 17,
            lineHeight: 1.6,
            color: colors.bone,
            maxWidth: 560,
            marginBottom: spacing.xl,
            textShadow: '0 1px 8px rgba(0,0,0,0.45)',
          }}
        >
          Reserve a black-car experience tailored to your itinerary —
          every ride private, every detail handled.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.md, alignItems: 'center' }}>
          <a
            href="#reserve"
            data-testid="hero-cta-reserve"
            style={{
              background: colors.champagne,
              color: colors.void,
              paddingTop: 20,
              paddingBottom: 20,
              paddingLeft: 38,
              paddingRight: 38,
              fontFamily: typography.label.family,
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              fontSize: 12,
              transition: `background ${motion.fast}ms ${motion.easeOut}, transform ${motion.fast}ms ${motion.easeOut}`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.champagneSoft;
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.champagne;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Begin Reservation
          </a>

          <a
            href="tel:+18442556771"
            data-testid="hero-cta-phone"
            style={{
              color: colors.bone,
              paddingTop: 20,
              paddingBottom: 20,
              paddingLeft: 28,
              paddingRight: 32,
              fontFamily: typography.label.family,
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              fontSize: 12,
              border: `1px solid ${colors.hairline}`,
              transition: `border-color ${motion.fast}ms ${motion.easeOut}, color ${motion.fast}ms ${motion.easeOut}`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.champagne;
              e.currentTarget.style.color = colors.champagne;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.hairline;
              e.currentTarget.style.color = colors.bone;
            }}
          >
            <span aria-hidden style={{ fontSize: 14 }}>☎</span>
            844-BLK-OPS1
          </a>
        </div>

        <div
          data-testid="hero-footnote"
          style={{
            ...label,
            color: colors.ash,
            marginTop: spacing.xxl,
            fontSize: 10,
            letterSpacing: '0.4em',
          }}
        >
          24/7 Concierge <span style={{ color: colors.gold, margin: '0 8px' }}>·</span> By Appointment Only
        </div>
      </div>
    </section>
  );
}

/* Services */
const SERVICES = [
  { code: 'S-01', title: 'Airport Transfer',     body: 'Curbside arrivals coordinated to your flight in real time. Meet-and-greet inside the terminal on request. Luggage assistance standard.', bullets: ['Live flight tracking', 'Meet & greet', '90-min grace'] },
  { code: 'S-02', title: 'Corporate Travel',     body: 'Discreet door-to-door transport for executives, boards, and delegations. Centralized billing, NDA-bound chauffeurs, multi-city dispatch.', bullets: ['Centralized billing', 'NDA chauffeurs', 'Multi-city dispatch'] },
  { code: 'S-03', title: 'Special Events',       body: 'Weddings, galas, premieres, and arrivals where presence matters. Coordinated detail with red-carpet timing and venue liaison.', bullets: ['Venue liaison', 'Red-carpet timing', 'Convoy on request'] },
  { code: 'S-04', title: 'Hourly As-Directed',   body: 'A chauffeur and vehicle at your disposal — by the hour or by the day. Re-route on demand. Wait time included, no surge pricing.', bullets: ['No surge pricing', 'Wait time included', 'Re-route on demand'] },
];

function Services() {
  return (
    <section
      id="services"
      data-testid="services"
      style={{
        paddingTop: spacing.xxxl,
        paddingBottom: spacing.xxxl,
        background: colors.void,
        borderTop: `1px solid ${colors.hairlineSoft}`,
      }}
    >
      <div style={container}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: spacing.lg,
            marginBottom: spacing.xl,
          }}
        >
          <div style={{ maxWidth: 720 }}>
            <div style={{ ...label, marginBottom: 14 }}>§ 02 — Services</div>
            <h2 style={{ ...display, fontSize: 'clamp(34px, 4.6vw, 60px)', margin: 0 }}>
              Tailored ground transport,<br />
              <em style={{ color: colors.champagne, fontStyle: 'italic' }}>handled end-to-end.</em>
            </h2>
          </div>
          <p style={{ fontFamily: typography.body.family, color: colors.ash, maxWidth: 360, lineHeight: 1.65, margin: 0, fontSize: 15 }}>
            Every reservation is dispatched by a senior concierge.
            One name, one number — and a chauffeur who already knows
            where the car needs to be.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 1,
            background: colors.hairlineSoft,
            border: `1px solid ${colors.hairlineSoft}`,
          }}
        >
          {SERVICES.map((s) => (
            <article
              key={s.code}
              data-testid={`service-${s.code}`}
              style={{
                position: 'relative',
                background: colors.void,
                padding: spacing.xl,
                transition: `background ${motion.base}ms ${motion.easeOut}`,
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = colors.onyx)}
              onMouseLeave={(e) => (e.currentTarget.style.background = colors.void)}
            >
              <div style={{ ...label, position: 'absolute', top: 18, right: 22, color: colors.goldDim, fontSize: 10 }}>
                {s.code}
              </div>
              <h3
                style={{
                  fontFamily: typography.display.family,
                  fontWeight: 500,
                  fontSize: 28,
                  color: colors.paper,
                  marginTop: 28,
                  marginBottom: 16,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.1,
                }}
              >
                {s.title}
              </h3>
              <p
                style={{
                  fontFamily: typography.body.family,
                  fontSize: 14.5,
                  lineHeight: 1.65,
                  color: colors.ash,
                  margin: 0,
                  marginBottom: spacing.lg,
                }}
              >
                {s.body}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {s.bullets.map((b) => (
                  <li
                    key={b}
                    style={{
                      ...label,
                      fontSize: 10,
                      color: colors.bone,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      letterSpacing: '0.22em',
                    }}
                  >
                    <span style={{ display: 'inline-block', width: 16, height: 1, background: colors.champagne }} />
                    {b}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Fleet */
const FLEET = [
  { code: 'F-01', title: 'Cadillac Escalade',   caption: 'Blacked exterior · premium leather interior · onboard refreshments. Our signature vehicle, prepared and detailed before every reservation.', passengers: '1–6', luggage: '4 large', photo: imagery.fleetSuvPhoto },
  { code: 'F-02', title: 'Mercedes Sprinter',   caption: 'A first-class Sprinter for groups, executive delegations, and multi-stop itineraries. Reclining leather captains, workstation lighting, quiet cabin.', passengers: '1–8', luggage: '8 large', photo: imagery.fleetVanPhoto },
];

function Fleet() {
  return (
    <section
      id="fleet"
      data-testid="fleet"
      style={{
        paddingTop: spacing.xxxl,
        paddingBottom: spacing.xxxl,
        background: colors.onyx,
        borderTop: `1px solid ${colors.hairlineSoft}`,
      }}
    >
      <div style={container}>
        <div style={{ ...label, marginBottom: 14 }}>§ 03 — The Fleet</div>
        <h2 style={{ ...display, fontSize: 'clamp(34px, 4.6vw, 60px)', margin: 0, marginBottom: spacing.xl, maxWidth: 820 }}>
          Prepared before every reservation.<br />
          <em style={{ color: colors.champagne, fontStyle: 'italic' }}>Expanding with care.</em>
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: spacing.lg }}>
          {FLEET.map((v) => (
            <article
              key={v.code}
              data-testid={`fleet-${v.code}`}
              style={{
                background: colors.pewter,
                border: `1px solid ${colors.steel}`,
                overflow: 'hidden',
                transition: `transform ${motion.base}ms ${motion.easeOut}, border-color ${motion.base}ms ${motion.easeOut}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.champagne;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.steel;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div
                style={{
                  width: '100%',
                  aspectRatio: '16 / 10',
                  backgroundColor: colors.void,
                  backgroundImage: `linear-gradient(180deg, rgba(10,10,11,0) 40%, rgba(10,10,11,0.65) 100%), url("${v.photo}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div style={{ padding: spacing.lg }}>
                <div style={{ ...label, color: colors.gold, marginBottom: 8, fontSize: 10 }}>{v.code}</div>
                <h3
                  style={{
                    fontFamily: typography.display.family,
                    fontWeight: 500,
                    fontSize: 26,
                    margin: 0,
                    color: colors.paper,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {v.title}
                </h3>
                <p
                  style={{
                    fontFamily: typography.body.family,
                    color: colors.ash,
                    fontSize: 14,
                    marginTop: 8,
                    marginBottom: spacing.md,
                    lineHeight: 1.55,
                  }}
                >
                  {v.caption}
                </p>
                <div style={{ display: 'flex', gap: spacing.lg, ...label, color: colors.bone, fontSize: 10 }}>
                  <span><span style={{ color: colors.gold }}>Passengers</span> &nbsp;{v.passengers}</span>
                  <span><span style={{ color: colors.gold }}>Luggage</span> &nbsp;{v.luggage}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Pillars */
function Pillars() {
  const items = [
    ['01', 'Discretion',     'No identifying decals. No social posts. Your itinerary stays inside the dispatch room.'],
    ['02', 'Punctuality',    'On-site fifteen minutes early, every time. We track traffic, weather, and your calendar.'],
    ['03', 'Vetted Chauffeurs', 'Background-checked, licensed, and professionally trained. Every detail vetted before your reservation.'],
    ['04', 'Concierge Access',  'One number reaches your dedicated concierge. Restaurant holds, jet liaison, courier runs.'],
  ];
  return (
    <section
      id="doctrine"
      data-testid="pillars"
      style={{
        paddingTop: spacing.xxxl,
        paddingBottom: spacing.xxxl,
        background: colors.void,
        borderTop: `1px solid ${colors.hairlineSoft}`,
      }}
    >
      <div style={container}>
        <div style={{ ...label, marginBottom: 14 }}>§ 04 — Why Black-Ops</div>
        <h2 style={{ ...display, fontSize: 'clamp(34px, 4.6vw, 60px)', margin: 0, marginBottom: spacing.xl, maxWidth: 920 }}>
          Built around four <em style={{ color: colors.champagne, fontStyle: 'italic' }}>non-negotiables.</em>
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: spacing.xl }}>
          {items.map(([num, title, body]) => (
            <div key={num} data-testid={`pillar-${num}`}>
              <div
                style={{
                  fontFamily: typography.display.family,
                  fontWeight: 500,
                  fontSize: 56,
                  color: colors.champagne,
                  lineHeight: 1,
                  marginBottom: spacing.md,
                }}
              >
                {num}
              </div>
              <h3
                style={{
                  fontFamily: typography.display.family,
                  fontWeight: 500,
                  fontSize: 24,
                  color: colors.paper,
                  margin: 0,
                  marginBottom: 10,
                  letterSpacing: '-0.01em',
                }}
              >
                {title}
              </h3>
              <p style={{ fontFamily: typography.body.family, fontSize: 14.5, lineHeight: 1.65, color: colors.ash, margin: 0 }}>
                {body}
              </p>
            </div>
          ))}
        </div>

        <div style={{ ...hairline, marginTop: spacing.xxl, marginBottom: spacing.lg }} />

        <blockquote
          data-testid="pillars-quote"
          style={{
            margin: 0,
            maxWidth: 820,
            fontFamily: typography.display.family,
            fontWeight: 500,
            fontStyle: 'italic',
            fontSize: 'clamp(22px, 2.6vw, 34px)',
            lineHeight: 1.35,
            color: colors.bone,
          }}
        >
          <span style={{ color: colors.champagne }}>&ldquo;</span>
          The best ride is the one you forget you booked — because nothing went wrong.
          <span style={{ color: colors.champagne }}>&rdquo;</span>
          <div style={{ ...label, color: colors.gold, marginTop: spacing.md }}>
            — Director of Concierge, Black-Ops
          </div>
        </blockquote>
      </div>
    </section>
  );
}

/* Membership */
const TIERS = [
  { key: 'standard', name: 'Pay Per Ride',     price: 'No fee',     cadence: 'Reserve as needed',                       perks: ['Full concierge dispatch', 'Email confirmation & receipt', 'Transparent flat rates', 'Gratuity included'] },
  { key: 'founding', name: 'Founding Client',  price: 'Invitation', cadence: 'Limited to our first 50', featured: true, perks: ['Locked-in rates as we grow', 'Priority dispatch', 'Direct line to the dispatch desk', 'Early access as the fleet expands', 'A standing introduction to your chauffeur'] },
];

function Membership() {
  return (
    <section
      id="membership"
      data-testid="membership"
      style={{
        paddingTop: spacing.xxxl,
        paddingBottom: spacing.xxxl,
        background: colors.onyx,
        borderTop: `1px solid ${colors.hairlineSoft}`,
      }}
    >
      <div style={container}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: spacing.lg,
            marginBottom: spacing.xl,
          }}
        >
          <div>
            <div style={{ ...label, marginBottom: 14 }}>§ 05 — Membership</div>
            <h2 style={{ ...display, fontSize: 'clamp(34px, 4.6vw, 60px)', margin: 0, maxWidth: 760 }}>
              Two ways to ride.<br />
              <em style={{ color: colors.champagne, fontStyle: 'italic' }}>One standard of service.</em>
            </h2>
          </div>
          <p style={{ fontFamily: typography.body.family, color: colors.ash, maxWidth: 360, lineHeight: 1.65, margin: 0, fontSize: 15 }}>
            We are an Arizona-based boutique service. Founding Client status is
            reserved for our first fifty patrons — by invitation, and by introduction.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: spacing.lg }}>
          {TIERS.map((tier) => (
            <article
              key={tier.key}
              data-testid={`tier-${tier.key}`}
              style={{
                position: 'relative',
                background: tier.featured ? colors.pewter : colors.void,
                border: `1px solid ${tier.featured ? colors.champagne : colors.steel}`,
                padding: spacing.xl,
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.md,
                transition: `transform ${motion.base}ms ${motion.easeOut}`,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              {tier.featured && (
                <div
                  style={{
                    ...label,
                    position: 'absolute',
                    top: -10,
                    left: 20,
                    background: colors.champagne,
                    color: colors.void,
                    paddingTop: 5,
                    paddingBottom: 5,
                    paddingLeft: 10,
                    paddingRight: 10,
                    fontSize: 9,
                  }}
                >
                  Founding 50
                </div>
              )}

              <div style={{ ...label, color: colors.gold, fontSize: 10 }}>{tier.name}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span
                  style={{
                    fontFamily: typography.display.family,
                    fontWeight: 500,
                    fontSize: 46,
                    color: colors.paper,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {tier.price}
                </span>
                <span style={{ fontFamily: typography.body.family, fontSize: 13, color: colors.ash }}>
                  {tier.cadence}
                </span>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {tier.perks.map((p) => (
                  <li
                    key={p}
                    style={{
                      fontFamily: typography.body.family,
                      fontSize: 14,
                      color: colors.bone,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      lineHeight: 1.5,
                    }}
                  >
                    <span aria-hidden style={{ color: colors.champagne, fontWeight: 600, flexShrink: 0, paddingTop: 3 }}>
                      ✦
                    </span>
                    {p}
                  </li>
                ))}
              </ul>

              <a
                href="#reserve"
                data-testid={`tier-cta-${tier.key}`}
                style={{
                  marginTop: 'auto',
                  textAlign: 'center',
                  paddingTop: 14,
                  paddingBottom: 14,
                  ...label,
                  color: tier.featured ? colors.void : colors.champagne,
                  background: tier.featured ? colors.champagne : 'transparent',
                  border: `1px solid ${colors.champagne}`,
                  transition: `background ${motion.fast}ms ${motion.easeOut}, color ${motion.fast}ms ${motion.easeOut}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.champagne;
                  e.currentTarget.style.color = colors.void;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = tier.featured ? colors.champagne : 'transparent';
                  e.currentTarget.style.color = tier.featured ? colors.void : colors.champagne;
                }}
              >
                {tier.key === 'founding' ? 'Request Invitation' : 'Begin Reservation'}
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Reservation */
const inputStyle = {
  background: colors.void,
  border: `1px solid ${colors.steel}`,
  color: colors.bone,
  padding: 14,
  fontFamily: typography.body.family,
  fontSize: 15,
  outline: 'none',
  borderRadius: 0,
  transition: `border-color ${motion.fast}ms ${motion.easeOut}`,
};

function Field({ label: lbl, name, type, testid, required = true, placeholder }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ ...label, color: colors.gold, fontSize: 10 }}>{lbl}</span>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        style={inputStyle}
        onFocus={(e) => (e.currentTarget.style.borderColor = colors.champagne)}
        onBlur={(e) => (e.currentTarget.style.borderColor = colors.steel)}
      />
    </label>
  );
}

function Reservation() {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting' || status === 'success') return;
    setStatus('submitting');
    setErrorMsg('');
    const data = new FormData(e.currentTarget);
    const payload = {
      name: (data.get('name') || '').toString().trim(),
      email: (data.get('email') || '').toString().trim(),
      phone: (data.get('phone') || '').toString().trim(),
      date: (data.get('date') || '').toString(),
      time: (data.get('time') || '').toString(),
      pickup: (data.get('pickup') || '').toString().trim(),
      dropoff: (data.get('dropoff') || '').toString().trim(),
      vehicle: (data.get('vehicle') || '').toString(),
      notes: (data.get('notes') || '').toString().trim(),
      sms_consent: data.get('smsConsent') === 'on',
    };
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        let msg = `Request failed (${res.status}).`;
        if (typeof detail.detail === 'string') {
          msg = detail.detail;
        } else if (Array.isArray(detail.detail) && detail.detail[0]) {
          const first = detail.detail[0];
          const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : 'field';
          msg = `Please check the "${field}" field: ${first.msg || 'invalid value'}.`;
        }
        throw new Error(msg);
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please call 844-BLK-OPS1.');
    }
  };

  const submitted = status === 'success';
  const submitting = status === 'submitting';

  return (
    <section
      id="reserve"
      data-testid="reservation"
      style={{
        paddingTop: spacing.xxxl,
        paddingBottom: spacing.xxxl,
        background: colors.void,
        borderTop: `1px solid ${colors.hairlineSoft}`,
      }}
    >
      <div
        className="reservation-grid"
        style={{
          ...container,
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1.2fr)',
          gap: spacing.xxl,
          alignItems: 'start',
        }}
      >
        <div>
          <div style={{ ...label, marginBottom: 14 }}>§ 06 — Reservation Inquiry</div>
          <h2 style={{ ...display, fontSize: 'clamp(32px, 4.4vw, 56px)', margin: 0, marginBottom: spacing.lg }}>
            Begin your<br />
            <em style={{ color: colors.champagne, fontStyle: 'italic' }}>reservation.</em>
          </h2>
          <p style={{ fontFamily: typography.body.family, color: colors.ash, lineHeight: 1.65, maxWidth: 460, marginBottom: spacing.xl, fontSize: 15 }}>
            A senior concierge will confirm your reservation within thirty minutes,
            day or night. For same-hour service please call the line below directly.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
            <div data-testid="contact-line">
              <div style={{ ...label, color: colors.gold, marginBottom: 6 }}>Concierge Line · 24/7</div>
              <a
                href="tel:+18442556771"
                style={{ fontFamily: typography.display.family, color: colors.paper, fontSize: 30, letterSpacing: '-0.01em' }}
              >
                844-BLK-OPS1
              </a>
            </div>
            <div data-testid="contact-email">
              <div style={{ ...label, color: colors.gold, marginBottom: 6 }}>Reservations</div>
              <a
                href="mailto:pickup@blk-ops1.com"
                style={{ fontFamily: typography.body.family, color: colors.bone, fontSize: 17 }}
              >
                pickup@blk-ops1.com
              </a>
            </div>
            <div data-testid="contact-hq">
              <div style={{ ...label, color: colors.gold, marginBottom: 6 }}>Headquarters</div>
              <div style={{ fontFamily: typography.body.family, color: colors.bone, fontSize: 15, lineHeight: 1.55 }}>
                Greater Phoenix, Arizona<br />
                Door-to-door across the Valley
              </div>
            </div>
          </div>
        </div>

        <form
          data-testid="reservation-form"
          onSubmit={onSubmit}
          style={{
            background: colors.onyx,
            border: `1px solid ${colors.steel}`,
            padding: spacing.xl,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.md,
          }}
        >
          <div style={{ ...label, color: colors.champagne }}>Reservation Request</div>

          <Field testid="field-name"  label="Full Name" name="name"  type="text" />
          <Field testid="field-email" label="Email"     name="email" type="email" />
          <Field testid="field-phone" label="Mobile"    name="phone" type="tel" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
            <Field testid="field-date" label="Date"        name="date" type="date" />
            <Field testid="field-time" label="Pickup Time" name="time" type="time" />
          </div>

          <Field testid="field-pickup"  label="Pickup Address"   name="pickup"  type="text" placeholder="e.g. Camelback Mountain Resort" />
          <Field testid="field-dropoff" label="Drop-off Address" name="dropoff" type="text" placeholder="e.g. PHX Sky Harbor · Terminal 4" />

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ ...label, color: colors.gold, fontSize: 10 }}>Vehicle</span>
            <select required name="vehicle" data-testid="field-vehicle" defaultValue="" style={inputStyle}>
              <option value="" disabled>Select…</option>
              <option>Cadillac Escalade</option>
              <option>Mercedes Sprinter</option>
              <option>No preference — recommend for me</option>
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ ...label, color: colors.gold, fontSize: 10 }}>Notes</span>
            <textarea
              name="notes"
              data-testid="field-notes"
              rows={4}
              placeholder="Flight number, child seats, multiple stops, signage preferences…"
              style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }}
            />
          </label>

          <label
            data-testid="sms-consent-row"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: spacing.md,
              border: `1px solid ${colors.steel}`,
              background: colors.void,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              name="smsConsent"
              data-testid="field-sms-consent"
              style={{
                marginTop: 3,
                width: 16,
                height: 16,
                accentColor: colors.champagne,
                flexShrink: 0,
                cursor: 'pointer',
              }}
            />
            <span
              style={{
                fontFamily: typography.body.family,
                color: colors.bone,
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              By checking this box, you agree to receive text messages related to
              Service Request Confirmation, Customer Support Response, General Inquiry
              Response, Customer Satisfaction Survey, General Customer Assistance,
              and Promotions or offers from Black-Ops Luxury Car Service. Reply{' '}
              <strong style={{ color: colors.champagne }}>STOP</strong> to opt out
              anytime. Text <strong style={{ color: colors.champagne }}>HELP</strong> to{' '}
              <a
                href="tel:+18442556771"
                data-testid="sms-consent-phone"
                style={{ color: colors.champagne, borderBottom: `1px solid ${colors.champagne}` }}
                onClick={(e) => e.stopPropagation()}
              >
                844-BLK-OPS1
              </a>{' '}
              for support. Message/data rates may apply. Frequency may vary. View our{' '}
              <a
                href="/privacy"
                data-testid="sms-consent-privacy-link"
                style={{ color: colors.champagne, borderBottom: `1px solid ${colors.champagne}` }}
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="/sms-terms"
                data-testid="sms-consent-terms-link"
                style={{ color: colors.champagne, borderBottom: `1px solid ${colors.champagne}` }}
                onClick={(e) => e.stopPropagation()}
              >
                SMS Terms &amp; Conditions
              </a>{' '}
              for details.
            </span>
          </label>

          <button
            type="submit"
            data-testid="reservation-submit"
            disabled={submitted}
            style={{
              marginTop: spacing.sm,
              background: submitted ? colors.gold : colors.champagne,
              color: colors.void,
              border: 'none',
              paddingTop: 18,
              paddingBottom: 18,
              fontFamily: typography.label.family,
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              fontSize: 12,
              cursor: submitted ? 'default' : 'pointer',
              transition: `background ${motion.fast}ms ${motion.easeOut}`,
            }}
          >
            {submitted ? '✓ Received — concierge will confirm < 30 min' : 'Begin Reservation'}
          </button>

          <div style={{ ...label, color: colors.ash, fontSize: 9, letterSpacing: '0.2em', lineHeight: 1.6 }}>
            Submitting this form does not finalize a reservation. A concierge
            will contact you to confirm availability and quote.
          </div>
        </form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      data-testid="footer"
      style={{
        borderTop: `1px solid ${colors.hairlineSoft}`,
        paddingTop: spacing.xl,
        paddingBottom: spacing.xl,
        background: colors.void,
      }}
    >
      <div style={{ ...container, display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: spacing.xl,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <BrandLogo variant="horizontal" size={170} color={colors.bone} taglineColor={colors.gold} />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xl, ...label, color: colors.bone, fontSize: 11 }}>
            <a href="#services">Services</a>
            <a href="#fleet">Fleet</a>
            <a href="#membership">Membership</a>
            <a href="#reserve">Reserve</a>
            <a href="/privacy" data-testid="footer-privacy">Privacy</a>
            <a href="/sms-terms" data-testid="footer-sms-terms">SMS Terms</a>
            <a href="tel:+18442556771">844-BLK-OPS1</a>
            <a href="mailto:pickup@blk-ops1.com">Email</a>
          </div>
        </div>

        <div style={hairline} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: spacing.md,
            ...label,
            color: colors.ash,
            fontSize: 10,
            letterSpacing: '0.3em',
          }}
        >
          <span>© {new Date().getFullYear()} Black-Ops Luxury Car Service, LLC.</span>
          <span>Licensed & insured in Arizona · By appointment only</span>
          <span>www.black-opslcs.com</span>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingPage() {
  return (
    <main
      data-testid="marketing-root"
      style={{
        backgroundColor: colors.void,
        color: colors.bone,
        minHeight: '100vh',
        fontFamily: typography.body.family,
      }}
    >
      <StatusStrip />
      <TopNav />
      <Hero />
      <Services />
      <Fleet />
      <Pillars />
      <Membership />
      <Reservation />
      <Footer />
    </main>
  );
}
